import { Button as DeprecatedButton } from 'ui-components/button/button';
import { CheckMarkIcon } from 'components/assets/Icons/Icons';
import React from 'react';
import {
    useEnableSelectionCheckbox,
    useToggleSelectedId,
} from 'features/explore/hooks/use-explore-selection';
import { PlaceOverlay } from 'ui-components/google-map-layers/types/google-map-layers-types';
import {
    useGetActiveOverlay,
    useComparePoiInfo,
} from 'features/explore/hooks/use-explore-overlay-poi';
import { Tooltip } from 'ui-components/tooltip/tooltip';
import { isPoiRestrictedCoordinate } from 'features/explore/utils/utils';
import { Spinner, SimpleTooltip, Button, Icon } from '@placer-ui/components';
import { useCheckCoordinateForNearbyReport } from 'features/explore/hooks/use-explore-nearby-activity';
import { useExplorePoiState } from 'features/explore/model/hooks/use-explore-poi-state';
import { useFullscreen } from '@advanced-mapping/pl-mapbox';
import { getFeatureFlags } from 'core/flow-control';
import classNames from 'classnames';
import styles from 'features/explore/components/explore-side-bar/components/explore-drawer/explore-drawer-option/option-body/explore-drawer-option-body.module.scss';

type ToggleCompareButtonProps = {
    poi: PlaceOverlay;
};

export const ToggleCompareButton = ({ poi }: ToggleCompareButtonProps) => {
    const { highlightedPoiId } = useExplorePoiState();
    const enableMultiselect = useEnableSelectionCheckbox(highlightedPoiId);
    const toggleId = useToggleSelectedId();
    const activeOverlay = useGetActiveOverlay()(poi);
    const { text: tooltipCheckbox, isLoading: isCoordinateLoading } =
        useCheckCoordinateForNearbyReport()(poi);
    const { isFullscreen } = useFullscreen();
    const comparePoiInfo = useComparePoiInfo();
    const isRestrictedCoordinate = isPoiRestrictedCoordinate(activeOverlay);
    const { accesibility_enable_explore_property_reports_ff } = getFeatureFlags();

    const isSelected = activeOverlay?.isSelected;
    const shouldEnableMultiselect = enableMultiselect({
        withHover: false,
        poi,
        activeOverlay,
    });
    const validPoiForCompareInfo = comparePoiInfo(poi);

    const isDisabled =
        !shouldEnableMultiselect || !validPoiForCompareInfo.enable || isRestrictedCoordinate;

    const handleToggle = () => !isRestrictedCoordinate && toggleId({ poi });

    if (accesibility_enable_explore_property_reports_ff) {
        return (
            <SimpleTooltip title={validPoiForCompareInfo.tooltipText ?? tooltipCheckbox}>
                <Button
                    onClick={handleToggle}
                    size="small"
                    disabled={isDisabled}
                    testId="add-compare-button-checkbox"
                    isLoading={isCoordinateLoading}
                    LeftIcon={<Icon type={isSelected ? 'MinusSquare' : 'PlusSquare'} />}
                    fullWidth
                    variant="secondary-gray"
                >
                    {`${isSelected ? 'Remove from' : 'Add to compare'} List`}
                </Button>
            </SimpleTooltip>
        );
    }

    return (
        <Tooltip
            small
            placement={'top'}
            title={validPoiForCompareInfo.tooltipText ?? tooltipCheckbox}
            useParentContainer={isFullscreen}
        >
            <span>
                <DeprecatedButton
                    data-testid={'add-compare-button'}
                    disabled={isDisabled}
                    onClick={handleToggle}
                    size="large"
                    block={true}
                    className={classNames(styles.multiselectAction, {
                        [styles.isSelected]: isSelected,
                        [styles.disabled]: isDisabled,
                    })}
                >
                    <div
                        className={classNames(styles.checkmark, {
                            [styles.isLoading]: isCoordinateLoading,
                        })}
                    >
                        {isCoordinateLoading ? <Spinner variant={'brand'} /> : <CheckMarkIcon />}
                    </div>
                    Add to Compare List
                </DeprecatedButton>
            </span>
        </Tooltip>
    );
};
